import type { TakeoffAiMessage } from './useTakeoffComputer';
import { MAX_TAKEOFF_GOALS, MAX_TAKEOFF_RETAINED_EXCLUSIONS } from './takeoffLimits';

export const RUN_PREFIX = 'quantis:run:v1:';
const GOALS_PREFIX = 'pluto:goals:v1:';
const COMPLETE_PREFIX = 'pluto:goal-complete:v1:';
const REVIEWED_PREFIX = 'pluto:goal-reviewed:v1:';

export interface TakeoffGoal {
  key: string;
  name: string;
  kind: 'count' | 'length' | 'area' | 'volume';
  complete: boolean;
  reviewed?: boolean;
  evidence?: string;
  view?: string;
}

export interface TakeoffGoalState {
  runId: string;
  scope: string;
  goals: TakeoffGoal[];
  excludedContext?: string[];
}

export function goalsEvent(runId: string, scope: string, goals: Omit<TakeoffGoal, 'complete'>[], excludedContext: string[] = []): string {
  if (goals.length > MAX_TAKEOFF_GOALS || excludedContext.length > MAX_TAKEOFF_RETAINED_EXCLUSIONS)
    throw new Error(`Goal history supports up to ${MAX_TAKEOFF_GOALS} goals and ${MAX_TAKEOFF_RETAINED_EXCLUSIONS} retained exclusions; scope cannot be silently truncated.`);
  return `${GOALS_PREFIX}${JSON.stringify({ runId, scope, goals,
    ...(excludedContext.length ? { excludedContext } : {}) })}`;
}

export function goalCompleteEvent(runId: string, key: string): string {
  return `${COMPLETE_PREFIX}${JSON.stringify({ runId, key })}`;
}

export function goalReviewedEvent(runId: string, key: string, reviewed: boolean): string {
  return `${REVIEWED_PREFIX}${JSON.stringify({ runId, key, reviewed })}`;
}

export function isGoalEvent(text: string): boolean {
  return text.startsWith(RUN_PREFIX) || text.startsWith(GOALS_PREFIX) || text.startsWith(COMPLETE_PREFIX) || text.startsWith(REVIEWED_PREFIX);
}

export function latestGoalState(messages: TakeoffAiMessage[]): TakeoffGoalState | null {
  let state: TakeoffGoalState | null = null;
  for (const message of messages) {
    // A new request starts a new run. Do not present an earlier plan as this
    // run's goals while inspection is in progress, or if planning fails.
    if (message.role === 'user') { state = null; continue; }
    if (message.role !== 'status') continue;
    if (message.text.startsWith(GOALS_PREFIX)) {
      try {
        const value = JSON.parse(message.text.slice(GOALS_PREFIX.length)) as Partial<TakeoffGoalState>;
        if (typeof value.runId !== 'string' || typeof value.scope !== 'string' || !Array.isArray(value.goals)) continue;
        if (value.goals.length > MAX_TAKEOFF_GOALS
          || (Array.isArray(value.excludedContext) && value.excludedContext.length > MAX_TAKEOFF_RETAINED_EXCLUSIONS)) {
          // An oversized snapshot cannot be displayed as a trimmed, apparently
          // complete plan, nor leave a superseded completed plan visible.
          state = null;
          continue;
        }
        const previousGoals: TakeoffGoal[] = state?.runId === value.runId ? state?.goals ?? [] : [];
        const goals: TakeoffGoal[] = value.goals.filter(goal => goal && typeof goal.key === 'string'
          && typeof goal.name === 'string' && ['count', 'length', 'area', 'volume'].includes(goal.kind))
          .map(goal => ({ key: goal.key, name: goal.name, kind: goal.kind,
            complete: previousGoals.some(previous => previous.key === goal.key && previous.complete),
            ...(previousGoals.some(previous => previous.key === goal.key && previous.reviewed)
              ? { reviewed: true } : {}),
            ...(typeof goal.evidence === 'string' ? { evidence: goal.evidence } : {}),
            ...(typeof goal.view === 'string' ? { view: goal.view } : {}) }));
        const excludedContext = Array.isArray(value.excludedContext)
          ? value.excludedContext.filter(item => typeof item === 'string') : [];
        state = { runId: value.runId, scope: value.scope, goals,
          ...(excludedContext.length ? { excludedContext } : {}) };
      } catch { /* Ignore malformed legacy status text. */ }
    } else if (state && message.text.startsWith(REVIEWED_PREFIX)) {
      try {
        const value = JSON.parse(message.text.slice(REVIEWED_PREFIX.length)) as { runId?: string; key?: string; reviewed?: boolean };
        if (value.runId === state.runId && typeof value.reviewed === 'boolean') state.goals = state.goals.map(goal =>
          goal.key === value.key ? { ...goal, reviewed: value.reviewed } : goal);
      } catch { /* Ignore malformed review status text. */ }
    } else if (state && message.text.startsWith(COMPLETE_PREFIX)) {
      try {
        const value = JSON.parse(message.text.slice(COMPLETE_PREFIX.length)) as { runId?: string; key?: string };
        if (value.runId === state.runId) state.goals = state.goals.map(goal =>
          goal.key === value.key ? { ...goal, complete: true } : goal);
      } catch { /* Ignore malformed legacy status text. */ }
    }
  }
  return state;
}
